/**
 * Grouping and derived-state helpers for chevron chat items
 * (`thinkingChevron` feature).
 *
 * These are pure functions over the app-level `ToolCall` / `ChatItem` types, so
 * they serve both the live and history feeds identically.
 */

import { ChatItem, ToolCall } from "@/types";

/**
 * Collapses consecutive calls that are the same tool **with the same detail**
 * into a single entry carrying a `repeatCount`.
 *
 * The detail must match, not just the tool name. Two `editor` calls that read
 * different files, or two searches with different queries, each carry
 * information the user wants — collapsing them to `Editor ×2` would discard the
 * more useful half. Only genuinely repeated work (identical tool, identical
 * summary, e.g. a retried call) is worth folding into a count.
 */
export function collapseConsecutive(calls: ToolCall[]): ToolCall[] {
  const collapsed: ToolCall[] = [];

  for (const call of calls) {
    const prev = collapsed[collapsed.length - 1];
    if (
      prev &&
      prev.toolName === call.toolName &&
      (prev.details ?? "") === (call.details ?? "")
    ) {
      prev.repeatCount = (prev.repeatCount ?? 1) + 1;
      // A group is only "done" once every member has finished. Any running
      // member keeps the group running; any error surfaces on the group.
      if (call.status === "running") {
        prev.status = "running";
      } else if (call.status === "error" && prev.status !== "running") {
        prev.status = "error";
      }
      continue;
    }
    collapsed.push({ ...call });
  }

  return collapsed;
}

/**
 * Collapses consecutive same-tool calls within every `tools` item.
 * Applied at render time so the underlying per-call state stays intact.
 */
export function collapseChatItems(items: ChatItem[]): ChatItem[] {
  return items.map((item) =>
    item.kind === "tools"
      ? { kind: "tools", calls: collapseConsecutive(item.calls) }
      : item,
  );
}

/** Concatenates the text of `chatItems` — used to keep `Message.content` populated. */
export function chatItemsToText(items: ChatItem[]): string {
  return items
    .filter(
      (item): item is { kind: "text"; content: string } => item.kind === "text",
    )
    .map((item) => item.content)
    .join("\n");
}

/** True when any tool call in `items` is still running. */
export function hasRunningToolCall(items: ChatItem[]): boolean {
  return items.some(
    (item) =>
      item.kind === "tools" && item.calls.some((c) => c.status === "running"),
  );
}

/** The `details` of the newest running tool call, for the working indicator. */
export function activeToolSummary(items: ChatItem[]): string | undefined {
  for (let i = items.length - 1; i >= 0; i--) {
    const item = items[i];
    if (item.kind !== "tools") continue;
    for (let j = item.calls.length - 1; j >= 0; j--) {
      if (item.calls[j].status === "running") {
        return item.calls[j].details;
      }
    }
  }
  return undefined;
}
